// ============================================================================
// PairsForm — Bascule implicite de casquette
// ============================================================================
// @systeme: scoping-org
//
// PROBLÈME (constat FX 2026-08-19, recette animateur Rouge).
// Une personne peut être participante d'une organisation et animatrice d'une
// autre : alumni de Cecodev devenu animateur de Cecodev_Inter, ou stagiaire du
// Module Vert devenu animateur de son employeur Intra — ce second cas ayant
// vocation à être le plus courant. Techniquement elle a UN compte, N
// `user_memberships`, et des rôles portés par le membership ; le JWT n'en
// scope qu'un à la fois (invariant P8). Résultat vécu : entrée d'un côté, elle
// perdait l'autre, et devait comprendre nos organisations pour s'en sortir.
// « Cet utilisateur n'a aucune idée de comment sont organisées les activités
// dans les organisations. Cela doit être transparent pour lui. »
//
// SOLUTION RETENUE (arbitrage FX 2026-08-19) : l'org suit la casquette.
//   - le menu PERSONNEL est l'union des casquettes (cf. menu-config) ; chaque
//     entrée sait, le cas échéant, quel membership l'anime (`membershipId`) ;
//   - ce hook regarde l'URL courante : si elle relève d'une entrée portée par
//     un AUTRE membership, il re-signe le JWT vers celui-ci avant de laisser la
//     page se rendre.
//
// Pourquoi au niveau de la ROUTE et non du clic : une page personnelle
// s'atteint aussi par un lien profond, un favori, un bouton interne ou une
// action du cockpit. Un handler de clic dans la barre latérale n'en couvrirait
// qu'une partie ; l'URL les couvre toutes. La table de correspondance
// route → membership n'est pas un registre de plus : elle est DÉRIVÉE du menu
// déjà construit, seule source de vérité.
//
// Rapport avec `useProposerInter` (2026-08-06) : ce hook-là fait la même
// bascule, mais AVANT une action précise (« Proposer un groupe ») vers une page
// qui n'est PAS dans le menu — il reste donc nécessaire et n'est pas un doublon.
// Règle : bascule au niveau d'une ROUTE DE MENU → ici ; bascule avant une action
// ponctuelle vers une page hors menu → un hook d'action dédié. Pas de troisième
// mécanique (P6).
//
// TENTATIVE RETIRÉE le 2026-08-19 (même journée) — à ne pas refaire. On avait
// étendu ce hook aux pages d'ADMINISTRATION org-typées (« Inter › Animation »
// n'a de sens que sur la marketplace) via un second champ `membershipRequis`,
// honoré même pour les administrateurs. Techniquement correct, humainement
// mauvais : FX ne savait plus dans quelle org il se trouvait — « je vois mon
// axe de Cecodev_Inter quand je suis dans Cecodev mais plus quand je suis dans
// Cecodev_Inter ». Un administrateur lit son org active pour savoir ce que ses
// gestes suivants vont affecter ; la lui déplacer en silence lui retire ce
// repère, et le gain de confort ne vaut pas cette perte.
//
// La leçon vaut au-delà de ce cas : la bascule silencieuse n'est acceptable que
// pour qui ne connaît PAS nos organisations (le participant-animateur), pas
// pour qui les pilote. Le besoin réel — « je ne vois rien et je ne sais pas
// pourquoi » — se traite en SIGNALANT (bandeau + bouton de bascule sur la page
// concernée), pas en décidant à la place.
//
// Ce que ce hook ne fait PAS : élargir des droits. Le serveur continue de
// n'autoriser que ce que porte le JWT courant ; on choisit lequel des JWT
// légitimes de la personne est actif, rien d'autre. Les espaces d'ADMINISTRATION
// ne portent jamais de `membershipId` — y entrer reste un geste explicite.

import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useOrganisation } from "../contexts/OrganisationContext";
import type { MenuSection } from "../lib/navigation/types";

/**
 * Rôles pour qui l'organisation active est un OUTIL DE TRAVAIL, pas un détail
 * d'implémentation : un pilote sait dans quelle org il est, il l'a choisie, et
 * son menu d'administration en dépend. Lui déplacer l'org sous les pieds parce
 * qu'il consulte une page personnelle serait une régression, pas un service.
 * La bascule implicite ne concerne donc que les casquettes non administratives
 * — c'est-à-dire exactement la population qui ne connaît pas nos organisations.
 */
const ROLES_ADMINISTRATIFS = ["SUPER_ADMIN", "PILOTE"];

/** Une entrée de menu couvre-t-elle ce chemin (elle-même ou ses enfants) ? */
function couvre(href: string, pathname: string): boolean {
  const base = href.split("?")[0];
  if (base === "#" || base === "/") return false;
  return pathname === base || pathname.startsWith(base + "/");
}

/**
 * Membership à activer pour la route courante, ou null s'il n'y a rien à faire.
 * En cas d'ambiguïté (deux entrées couvrent le chemin), la PLUS SPÉCIFIQUE
 * gagne — « /espace-animateur/administration » l'emporte sur
 * « /espace-animateur ».
 */
function membershipCible(sections: MenuSection[], pathname: string): string | null {
  let meilleur: { longueur: number; membershipId?: string } | null = null;
  for (const section of sections) {
    for (const item of section.items ?? []) {
      if (item.disabled || !couvre(item.href, pathname)) continue;
      const longueur = item.href.split("?")[0].length;
      if (!meilleur || longueur > meilleur.longueur) {
        meilleur = { longueur, membershipId: item.membershipId };
      }
    }
  }
  return meilleur?.membershipId ?? null;
}

/**
 * Bascule l'org active sur la casquette qu'exige la route courante.
 *
 * Retourne `true` pendant la bascule : l'appelant doit alors surseoir au rendu
 * de la page, sinon celle-ci partirait chercher ses données avec l'ancien
 * jeton et afficherait un écran vide avant de se corriger.
 */
export function useCasquetteImplicite(sections: MenuSection[]): boolean {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const { switchMembership, activeMembership } = useOrganisation();
  const [bascule, setBascule] = useState(false);
  // Bascules ÉCHOUÉES, pour ne pas marteler le serveur en boucle sur la même
  // route. On n'y inscrit QUE les échecs : une bascule réussie n'a pas à être
  // retenue (elle se neutralise d'elle-même, l'org active devenant la cible),
  // et l'inscrire empêcherait de rebasculer si la personne repasse à la main
  // sur son autre casquette avant de revenir ici.
  const echecs = useRef<Set<string>>(new Set());

  useEffect(() => {
    if ((user?.roles ?? []).some((r) => ROLES_ADMINISTRATIFS.includes(r))) return;
    // Une immersion est un geste délibéré d'administrateur : on n'en sort pas
    // dans son dos.
    if (user?.impersonating) return;

    const cible = membershipCible(sections, pathname);
    if (!cible || cible === activeMembership?.id) return;

    const cle = `${pathname}|${cible}`;
    if (echecs.current.has(cle)) return;

    let annule = false;
    setBascule(true);
    void switchMembership(cible)
      .catch(() => {
        // Échec silencieux : la page se rendra avec la casquette courante et
        // dira elle-même ce qu'elle ne peut pas montrer. Mieux vaut une page
        // pauvre qu'un écran de bascule bloqué.
        echecs.current.add(cle);
      })
      .finally(() => {
        if (!annule) setBascule(false);
      });

    return () => {
      annule = true;
    };
  }, [pathname, sections, activeMembership?.id, switchMembership, user?.roles, user?.impersonating]);

  return bascule;
}
